import React, { useEffect, useState } from "react";

import BasePadding from "@cayuse/shared-react.components.base-padding";
import Breadcrumbs from "@cayuse/shared-react.components.breadcrumbs";
import Button from "@cayuse/shared-react.components.button";
import IconButton from "@cayuse/shared-react.components.icon-button";
import SectionHeader from "@cayuse/shared-react.components.section-header";
import { Form } from "@flowable/forms";
import { api } from "services";

import ConfirmationModal from "components/ConfirmationModal";
import AddResearchTeamMember from "components/flowable/AddResearchTeamMember/AddResearchTeamMember";
import AnimalProtocolSearch from "components/flowable/AnimalProtocolSearch/AnimalProtocolSearch";
import Checkboxes from "components/flowable/Checkboxes";
import COIComponent from "components/flowable/COIComponent/COIComponent";
import CopyToFrom from "components/flowable/CopyToFrom";
import DataTable from "components/flowable/DataTable";
import { ExpressionField } from "components/flowable/ExpressionField";
import { FileInput } from "components/flowable/FileInput";
import FlowableOrgSearch from "components/flowable/FlowableOrgSearch";
import FlowablePersonSearch from "components/flowable/FlowablePersonSearch";
import FlowableRichText from "components/flowable/FlowableRichText";
import FlowableUnitSearch from "components/flowable/FlowableUnitSearch";
import HaploCostingTool from "components/flowable/HaploCostingTool/HaploCostingTool";
import HaploEthicsMonitor from "components/flowable/HaploEthicsMonitor/HaploEthicsMonitor";
import HaploRepository from "components/flowable/HaploRepository/HaploRepository";
import HazardSafetySearch from "components/flowable/HazardSafetySearch/HazardSafetySearch";
import HumanEthicsStudySearch from "components/flowable/HumanEthicsStudySearch/HumanEthicsStudySearch";
import IRBStudy from "components/flowable/IRBStudy/IRBStudy";
import NumberComponent from "components/flowable/Number";
import PeriodsDefinitionForm from "components/flowable/PeriodsDefinitionForm/PeriodsDefinitionForm";
import ProposalsS2SRecord from "components/flowable/ProposalsS2SRecord/ProposalsS2SRecord";
import ResearchTeam from "components/flowable/ResearchTeam";
import ResearchTeamBudget from "components/flowable/ResearchTeamBudget/ResearchTeamBudget";
import ResearchTeamBudgetImporter from "components/flowable/ResearchTeamBudgetImporter";
import SelectedRateTypeTable from "components/flowable/SelectedRateTypeTable/SelectedRateTypeTable";
import SFIComponent from "components/flowable/SFIComponent/SFIComponent";
import SummaryBudget from "components/flowable/SummaryBudget";
import SPLink from "components/SPLink";
import { FORMS_BASE_ROUTE } from "globals/constants";
import { useDictionary } from "hooks";
import { spUrl, notifyError } from "utils";
import { executeRequest } from "utils/proxy";

import { additionalData } from "../additionalData";
import FormHistoryMenu from "./FormHistoryMenu";
import styles from "./FormHistoryView.css";



const FormHistoryView = ({ match }) => {
	const dictionary = useDictionary("formManagerHistory");

	const [ previewPayload, previewOnChange ] = useState({});
	const [ formDefinitions, setFormDefinitions ] = useState(null);
	const [ formModel, setFormModel ] = useState(null);
	const [ previewFormDefinition, setPreviewFormDefinition ] = useState(null);
	const [ previewFormDefinitionId, setPreviewFormDefinitionId ] = useState(null);
	const [ restoreRevisionConfirmationModalMessage, setRestoreRevisionConfirmationModalMessage ] = useState("");
	const [ showRestoreRevisionConfirmationModal, setShowRestoreRevisionConfirmationModal ] = useState(false);
	const [ updatePageFunction, setUpdatePageFunction ] = useState({
		updatePageFunction: () => {
		}
	});

	useEffect(() => {
		initialize();
	}, []);	// eslint-disable-line

	const initialize = async () => {
		try {
			const formModel = await getFormModelByModelId(match?.params?.formId);

			setFormModel(formModel);

			const [ formDefinitionsData, model ] = await Promise.all([
				api.form.findDefinitions({
					key: formModel.modelKey,
					sort: "version",
					order: "desc",
				}),
				api.form.getSimplifiedModel(formModel?.modelId),
			]);

			let formDefinitions = formDefinitionsData.data;

			if (!formDefinitions || formDefinitions.length === 0) {
				// Add draft if the form has never been published
				formDefinitions = [ {
					isDraft: true,
					modelId: model.id,
					version: 1
				} ];
			} else if (formDefinitions && formDefinitions[0]) {
				const deploymentId = formDefinitions[0].deploymentId;

				const deployment = await api.form.getDeployment({ id: deploymentId });

				if (new Date(model.lastUpdated) > new Date(deployment.deploymentTime)) {
					// Add draft if the form has not been published yet
					formDefinitions.unshift({
						isDraft: true,
						modelId: formModel?.modelId,
						version: formDefinitions[0].version + 1
					});
				}
			}

			renderPreview(formDefinitions[0]);
			setFormDefinitions(formDefinitions);
		} catch (error) {
			notifyError("Something went wrong. Please try again.", error);
		}
	};

	const fetchData = (url, options) => {
		return executeRequest(url, options)
			.then(data => {
				// @flowable/forms 3.17's Fetch contract expects headers + status
				// (Pick<Response, "json"|"text"|"headers"|"status">). A resolved value is
				// always a 2xx since executeRequest throws on non-2xx.
				const isJson = data !== null && typeof data === "object";

				return {
					text: () => Promise.resolve(JSON.stringify(data)),
					json: () => Promise.resolve(data),
					headers: new Headers({"Content-Type": isJson ? "application/json" : "text/plain"}),
					status: 200,
				};
			});
	};

	const getFormModelByModelId = id => {
		return api.form.getDesignModel({ id });
	};

	const getIndexOfCurrentRevision = () => {
		if (formDefinitions) {
			return formDefinitions.findIndex(formDefinition => {
				return formDefinition.id === previewFormDefinitionId;
			});
		}

		return -1;
	};


	const isNextButtonDisabled = () => {
		if (formDefinitions) {
			return getIndexOfCurrentRevision() >= formDefinitions.length - 1;
		}

		return true;
	};

	const isPreviousButtonDisabled = () => getIndexOfCurrentRevision() <= 0;

	const isRestoreRevisionButtonDisabled = () => {
		if (formDefinitions) {
			return getIndexOfCurrentRevision() === 0;
		}

		return false;
	};

	const onConfirmRestoreRevision = async isConfirmed => {
		if (isConfirmed) {
			await api.form.updateSimplifiedModel(match?.params?.formId, previewFormDefinition);

			restoreInitialState();
			initialize();
		} else {
			setShowRestoreRevisionConfirmationModal(false);
		}
	};

	const restoreInitialState = () => {
		previewOnChange({});
		setFormDefinitions(null);
		setFormModel(null);
		setPreviewFormDefinition(null);
		setPreviewFormDefinitionId(null);
		setRestoreRevisionConfirmationModalMessage("");
		setShowRestoreRevisionConfirmationModal(false);
	};


	const renderPreview = formDefinition => {
		let promise;

		if (formDefinition.isDraft) {
			promise = api.form.getSimplifiedModel(formDefinition.modelId);
		} else {
			promise = api.form.getModel(formDefinition.id);
		}

		return promise.then(data => {
			setPreviewFormDefinition({ rows: data.rows });
			setPreviewFormDefinitionId(formDefinition.id);
		});
	};

	const selectRevision = async indexChange => {
		const currentIndex = getIndexOfCurrentRevision();
		const nextFormDefinition = formDefinitions[currentIndex + indexChange];

		if (nextFormDefinition) {
			await renderPreview(nextFormDefinition);
			updatePageFunction.updatePageFunction();
		}
	};

	const showRestoreRevisionConfirmationDialog = () => {
		const formDefinition = formDefinitions.find(formDefinition => {
			return formDefinition.id === previewFormDefinitionId;
		});

		setRestoreRevisionConfirmationModalMessage(
			`${dictionary.restoreRevisionConfirmationModalMessage
				|| "Any changes made to your draft revision will be lost. Are you sure you want to restore Revision"} ${formDefinition?.version}?`
		);
		setShowRestoreRevisionConfirmationModal(true);
	};


	const goBackButton = (
		<SPLink to={spUrl(`${FORMS_BASE_ROUTE}/${formModel?.modelId}`)}>
			<IconButton
				icon="arrow left"
				labelPosition="left"
			>
				{dictionary.goBackButtonLabel || "Cancel and go back"}
			</IconButton>
		</SPLink>
	);
	const nextRevisionButton = (
		<Button disabled={isNextButtonDisabled()}
			onClick={() => selectRevision(1)}
		>
			{dictionary.nextButtonLabel || "Next"}
		</Button>
	);
	const previousRevisionButton = (
		<Button disabled={isPreviousButtonDisabled()}
			onClick={() => selectRevision(-1)}
		>
			{dictionary.previousButtonLabel || "Previous"}
		</Button>
	);
	const restoreRevisionButton = (
		<Button disabled={isRestoreRevisionButtonDisabled()}
			onClick={() => showRestoreRevisionConfirmationDialog()}
		>
			{dictionary.restoreRevisionButtonLabel || "Restore this revision"}
		</Button>
	);


	const breadcrumbs = (
		<Breadcrumbs breadcrumbs={[
			{
				key: 0,
				text: dictionary.breadcrumbRoot || "Template Manager",
				link: spUrl(FORMS_BASE_ROUTE)
			},
			{
				key: 1,
				text: formModel?.name,
				link: spUrl(`${FORMS_BASE_ROUTE}/${formModel?.modelId}`)
			},
			{
				key: 2,
				text: dictionary.revisionHistory || "Revision History"
			}
		]}
		/>
	);

	return (
		<div>
			<ConfirmationModal
				icon={"history"}
				message={restoreRevisionConfirmationModalMessage}
				title={dictionary.restoreRevisionConfirmationModalTitle || "Restore this revision"}
				visible={showRestoreRevisionConfirmationModal}
				onConfirm={() => onConfirmRestoreRevision(true)}
				onCancel={() => onConfirmRestoreRevision(false)}
			/>
			<SectionHeader
				header={dictionary.heading}
				breadcrumbs={breadcrumbs}
				buttons={[ goBackButton, previousRevisionButton, nextRevisionButton, restoreRevisionButton ]}
			/>
			<BasePadding>
				<div className={styles.columnsContainer}>
					{previewFormDefinition &&
					<div className={styles.columnsContainerPreview}>
						{
							<Form
								config={previewFormDefinition}
								payload={previewPayload}
								onChange={previewOnChange}
								additionalData={additionalData}
								debug={true}
								fetch={fetchData}
								Components={{
									researchTeam: ResearchTeam,
									dataTable: DataTable,
									copyToFrom: CopyToFrom,
									addResearchTeamMember: AddResearchTeamMember,
									periodsDefinitionForm: PeriodsDefinitionForm,
									selectedRateTypeTable: SelectedRateTypeTable,
									cayuseIrbStudy: IRBStudy,
									cayuseProposalsS2SRecord: ProposalsS2SRecord,
									cayuseResearchTeamBudget: ResearchTeamBudget, // No longer used in new forms, Leaving in for old forms (8/01/2021). Hidden from SFB Palette with option in component
									cayuseResearchTeamBudgetImporter: ResearchTeamBudgetImporter, // No longer used in new forms, Leaving in for old forms (8/01/2021). Hidden from SFB Palette with option in component
									cayuseHumanEthicsStudySearch: HumanEthicsStudySearch,
									cayuseAnimalOversight: AnimalProtocolSearch,
									cayuseHazardSafety: HazardSafetySearch,
									cayuseHaploCostingTool: HaploCostingTool,
									cayuseHaploEthicsMonitor: HaploEthicsMonitor,
									cayuseHaploRepository: HaploRepository,
									cayuseSignificantFinancialInterest: SFIComponent,
									cayuseSummaryBudgetImporter: SummaryBudget, // New budget Importer (8/01/2021)
									cayuseCheckboxes: Checkboxes,
									one: NumberComponent,
									cayuseCOIComponent: COIComponent,
									fileInput: FileInput,
									expressionField: ExpressionField,
									personFinder: FlowablePersonSearch,
									unitFinder: FlowableUnitSearch,
									orgFinder: FlowableOrgSearch,
									richText: FlowableRichText,
								}}
							/>
						}
					</div>
					}
					<div className={styles.columnsContainerMenu}>
						{formDefinitions &&
							<FormHistoryMenu
								formDefinitions={formDefinitions}
								previewFormDefinitionId={previewFormDefinitionId}
								updatePage={setUpdatePageFunction}
								onRenderPreview={renderPreview}
							>
							</FormHistoryMenu>}
					</div>
				</div>
			</BasePadding>
		</div>
	);
};

export default FormHistoryView;
